import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";

import TNTable from "@/components/TNTable";
import { CButton, CCol, CFormInput, CRow, CTooltip } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslationContext } from "@/context/TranslationContext";
import { TranslationService } from "@/api";
import { faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert"; // Import
import { toast } from "react-toastify";
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css
import { titles } from "@/libs/constant";
import {
  action_tooltip,
  breadcrumb_key,
  messages,
} from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import { useNavigate } from "react-router-dom";
import { translation } from "@/constants/translation/translation.translation";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ReadMore from "@/components/ReadMore";
import { optionPageRecords } from "@/config/options";
import { CustomInput } from "@/components";

/**
 * @name ListTranslationPage
 *
 * @description
 * The List component is a simple UI component that renders a translation list admin.
 * Currently, it just displays the text "Translation List" as a placeholder for the actual list content.
 * This component can be expanded in the future to display a list of translations or other related content.
 *
 * @returns {JSX.Element} The JSX representation of the Translation List admin.
 */
const ListTranslationPage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  /**
   * setting state values to default,
   */
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [pageRecordSize, setPageRecordSize] = useState("");

  // Clear localStorage and reset state when component unmounts
  useEffect(() => {
    return () => {
      // Clear localStorage
      delete localStorage.adminTranslationTable;
      
      // Clear timeouts
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);
  /**
   * This will clear all state values, and localstorage as well
   */
  const handleClearFilter = () => {
    // Clear all state values immediately
    setTempSearchText("");
    setPageRecordSize("");
    setSearchText("");
    setCurrentPage(1);
    
    // Force refresh trigger to ensure API is called
    setRefreshTrigger(prev => prev + 1);
  };
  const [paginationData, setPaginationData] = useState(null);

  /**
   * !This Function will call when user clicks on Edit Button
   * @param {*} tData which is current element of button
   */
  const handleEditClick = (tData) => {
    let translation_id = tData.currentTarget.getAttribute("translation_id");
    // Router.push(masters.translation.edit.replace(":id", translation_id));
  };
  /**
   * This will call an API to delete translation
   */
  const handleDeleteClick = (tData) => {
    let translation_id = tData.currentTarget.getAttribute("translation_id");
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-box">
            <div className="alert-popup">
            <FontAwesomeIcon
                className="alert-close"
                icon={faClose}
                onClick={() => {
                  onClose();
                }}
              />
              <h2 className="alert-title">
                {t(translation.list.delete_popup_heading_text)}
              </h2>
              <div className="d-flex gap-2 justify-content-center">
                <CButton
                  color="primary"
                  className="table-delete-button"
                  onClick={async () => {
                    try {
                      await TranslationService.delete(translation_id);
                      toast.success(t(messages.toast.translation.deleted));
                      onClose();
                      // Force refresh trigger to ensure API is called
                      setRefreshTrigger(prev => prev + 1);
                    } catch (error) {
                      toast.error(t(messages.toast.translation.deleteFailed));
                      console.error("Delete error:", error);
                    }
                  }}
                >
                  {t(translation.list.delete_popup_yes_btn_text)}
                </CButton>
                <CButton
                  className="table-primary-button"
                  color="danger"
                  onClick={onClose}
                >
                  {t(translation.list.delete_popup_no_btn_text)}
                </CButton>
              </div>
            </div>
          </div>
        );
      },
    });
  };

  /**
   * !This Block is making Headers for the column
   * @param Not Required
   */

  const columnsjson = [
    {
      id: "key",
      accessorKey: "key",
      header: () => (
        <div>{t(translation.list.table.key_table_col_heading)}</div>
      ),
      cell: ({ getValue, row }) => {
        return <div>{`${row.original.group || ""}.${getValue()}`}</div>;
      },
      enableSorting: true,
    },
    {
      id: "text",
      accessorKey: "text",
      header: () => (
        <div>{t(translation.list.table.value_table_col_heading)}</div>
      ),
      cell: ({ row }) => {
        const { translations } = row.original;

        if (!translations || translations.length === 0) return <div>—</div>;
        return (
          <div>
            {translations.map((translation) => (
              <div key={translation.id} className="d-flex gap-1">
                <strong>{translation.language?.name || "N/A"}:</strong>{" "}
                <ReadMore>{translation.value || "N/A"}</ReadMore>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: "actions",
      accessorKey: "actions",
      header: () => (
        <div className="text-center">
          {t(translation.list.table.action_table_col_heading)}
        </div>
      ),
      cell: ({ row }) => {
        const handleEditClick = (e) => {
          e.stopPropagation();
          let translation_id = e.currentTarget.getAttribute("translation_id");
          if (translation_id) {
            navigate(`/masters/translation/${translation_id}`);
          }
        };

        const handleDeleteClick = (e) => {
          e.stopPropagation();
          let translation_id = e.currentTarget.getAttribute("translation_id");
          confirmAlert({
            customUI: ({ onClose }) => {
              return (
                <div className="alert-box">
                  <div className="alert-popup">
                  <FontAwesomeIcon
                      className="alert-close"
                      icon={faClose}
                      onClick={() => {
                        onClose();
                      }}
                    />
                    <h2 className="alert-title">
                      {t(translation.list.delete_popup_heading_text)}
                    </h2>
                    <div className="d-flex gap-2 justify-content-center">
                      <CButton
                        color="primary"
                        className="table-delete-button"
                        onClick={async () => {
                          try {
                            await TranslationService.delete(translation_id);
                            toast.success(t(messages.toast.translation.deleted));
                            onClose();
                            // Force refresh trigger to ensure API is called
                            setRefreshTrigger(prev => prev + 1);
                          } catch (error) {
                            toast.error(t(messages.toast.translation.deleteFailed));
                            console.error("Delete error:", error);
                          }
                        }}
                      >
                        {t(translation.list.delete_popup_yes_btn_text)}
                      </CButton>
                      <CButton
                        className="table-primary-button"
                        color="danger"
                        onClick={onClose}
                      >
                        {t(translation.list.delete_popup_no_btn_text)}
                      </CButton>
                    </div>
                  </div>
                </div>
              );
            },
          });
        };

        return (
          <div className="d-flex gap-2 justify-content-center">
            <div
                className="table-action-text"
                onClick={handleEditClick}
                translation_id={row.original.uuid}
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  className="list-icon"
                />
              </div>
            <div
                className="table-action-text"
                onClick={handleDeleteClick}
                translation_id={row.original.uuid}
              >
                <FontAwesomeIcon 
                  icon={faTrash}
                  className="list-icon"
                />
              </div>
          </div>
        );
      },
    },
  ];

  const [translationList, setTranslationList] = useState([]);
  const [tempSearchText, setTempSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const searchTimeout = useRef(null);
  const searchInputRef = useRef(null);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setTempSearchText(searchValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setSearchText(searchValue);
      setCurrentPage(1);
    }, 400); // Delay before updating real searchText
  };

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const fetchTranslationList = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Build query parameters - only include search_text if there's actual text
      const params = {
        page: currentPage,
        limit: pageRecordSize || 10,
      };
      
      // Only add search_text if it's not empty
      if (searchText && searchText.trim()) {
        params.search_text = searchText.trim();
      }
      
      const response = await TranslationService.getList(params);
      if (response && response.data) {
        
        // Handle new response structure
        const translationsList = response.data.translations_list || [];
        
        // The data is already in the correct format for the table
        
        setTranslationList(translationsList);
        
        // Set pagination data to ensure table works properly
        const paginationInfo = {
          total: response.data.pagination?.total_items || response.data.total || translationsList.length,
          per_page: response.data.pagination?.per_page || pageRecordSize || 10,
          current_page: response.data.pagination?.current_page || currentPage,
          count: translationsList.length // This is important for TNTable
        };
        
        
        setPaginationData(paginationInfo);
      }
    } catch (error) {
      console.error("Translation API Error:", error);
      toast.error(t(messages.toast.translation.fetchFailed));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchText, pageRecordSize, t]);

  const columns = useMemo(() => columnsjson, []);

  useEffect(() => {
    fetchTranslationList();
  }, [currentPage, searchText, pageRecordSize, refreshTrigger]);

  /**
   * This function will set current page of table
   */
  const pageIndexHandle = (pageIndex) => {
    setCurrentPage(pageIndex + 1);
  };

  /**
   * This function will call on Add Translation button Click and will take user to the add page of Translations
   */
  const addTranslationHandle = () => {
    navigate(masters.translation.add);
  };

  /**
   * This function will call on sync button Click and will call a function to call api
   */
  const syncTranslationHandle = async () => {
    try {
      const response = await TranslationService.sync({
        is_return: 1,
        is_api: 0
      });
      toast.success(response.message || t(messages.toast.translation.synced));
      fetchTranslationList(); // Refresh the list
    } catch (error) {
      toast.error(t(messages.toast.translation.syncFailed));
      console.error("Sync error:", error);
    }
  };

  /**
   * This function will handle import from CSV functionality
   */
  const importTranslationHandle = () => {
    // Create file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      try {
        // Read file content
        const fileContent = await readFileContent(file);
        
        // Call import API with default overwrite_existing = false
        const response = await TranslationService.import({
          file_data: fileContent,
          overwrite_existing: false
        });
        
        toast.success(response.message || t(messages.toast.translation.imported));
        fetchTranslationList(); // Refresh the list
        
      } catch (error) {
        toast.error(t(messages.toast.translation.importFailed));
        console.error("Import error:", error);
      }
      
      // Clean up
      document.body.removeChild(fileInput);
    };
    
    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  /**
   * Helper function to read file content
   */
  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  /**
   * This function will handle export to CSV functionality
   */
  const exportTranslationHandle = async () => {
    try {
      // Get current filters for export
      const params = {};
      
      // Add search text if exists
      if (searchText && searchText.trim()) {
        params.search_text = searchText.trim();
      }
      // Call export API with blob response
      const blob = await TranslationService.export(params);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t(messages.toast.translation.exported));
    } catch (error) {
      toast.error(t(messages.toast.translation.exportFailed));
      console.error("Export error:", error);
    }
  };

  return (
    <>
      <div>
        <CRow className="list-align">
          <CCol lg={10} md={10} sm={12} xs={12} className="d-flex gap-2 justify-content-end flex-wrap">
            <CFormInput
              placeholder={t(translation.list.search_placeholder)}
              type="text"
              name="searchText"
              value={tempSearchText}
              onChange={handleSearch}
              className="filter-field-space form-control-sm"
              style={{ maxWidth: '300px' }}
              innerRef={searchInputRef}
            />
            <CButton 
              color="primary" 
              type="button" 
              className="filter-field-space" 
              onClick={handleClearFilter}
              style={{ display: tempSearchText.trim() ? 'inline-block' : 'none' }}
            >
              {t(messages.clearFilterBtnText)}
            </CButton>
            <CButton 
              color="primary" 
              type="button" 
              className="filter-field-space" 
              onClick={addTranslationHandle}
            >
              {t(translation.list.create_translation_btn_text)}
            </CButton>
            <CButton 
              color="primary" 
              type="button" 
              className="filter-field-space" 
              onClick={importTranslationHandle}
            >
              {t(translation.list.import_translation_btn_text)}
            </CButton>
            <CButton 
              color="primary" 
              type="button" 
              className="filter-field-space" 
              onClick={exportTranslationHandle}
            >
              {t(translation.list.export_translation_btn_text)}
            </CButton>
            <CButton 
              color="primary" 
              type="button" 
              className="filter-field-space" 
              onClick={syncTranslationHandle}
            >
              {t(translation.list.sync_translation_btn_text)}
            </CButton>
          </CCol>
        </CRow>
      </div>

      <TNTable
        columns={columns}
        data={translationList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          setCurrentPage(pageIndex + 1);
          // Scroll to top of the page when pagination changes
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }}
        t={t}
        pageIndexGet={currentPage - 1}
        key={Math.floor(Math.random() * (1000 - 1 + 1) + 1)}
        isLoading={isLoading}
        className="text-start table-sortable mt-3"
      />
    </>
  );
};

ListTranslationPage.propTypes = {
  columns: PropTypes.any,
  row: PropTypes.any,
  data: PropTypes.any,
  paginationData: PropTypes.any,
  t: PropTypes.func,
};

// Static data that would have been passed via getStaticProps
ListTranslationPage.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.translation?.list || 'Translation List',
    pathname: '/masters/translation/list',
    active: true,
  },
];

export default ListTranslationPage;
